import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabaseClient';

export interface DemoAuth {
  phone: string;
  userId: string;
  roles: ('patient' | 'caregiver')[];
  timestamp: number;
}

export interface AppAuthState {
  userId: string | null;
  roles: ('patient' | 'caregiver')[];
  isDemo: boolean;
  loading: boolean;
  isAuthenticated: boolean;
}

function getDemoAuth(): DemoAuth | null {
  try {
    const raw = localStorage.getItem('kc_demo_login');
    if (!raw) return null;
    const parsed = JSON.parse(raw) as DemoAuth;
    return parsed;
  } catch {
    return null;
  }
}

export { getDemoAuth };

export function clearDemoAuth() {
  localStorage.removeItem('kc_demo_login');
  localStorage.removeItem('kc_demo_user_id');
}

export function useAppAuth(requireAuth = true): AppAuthState {
  const navigate = useNavigate();
  const [state, setState] = useState<AppAuthState>({
    userId: null,
    roles: [],
    isDemo: false,
    loading: true,
    isAuthenticated: false,
  });

  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  useEffect(() => {
    async function checkAuth() {
      try {
        // 1. 실제 Supabase 세션 먼저 체크
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          const { data: profile } = await supabase
            .from('profiles')
            .select('roles, role, caregiver_number')
            .eq('id', session.user.id)
            .maybeSingle();
          // roles(배열)가 null이면 role(텍스트)을 배열로 변환
          const resolvedRoles: ('patient' | 'caregiver')[] = profile?.roles?.length
            ? (profile.roles as ('patient' | 'caregiver')[])
            : (profile?.role ? [profile.role as ('patient' | 'caregiver')] : []);
          setState({
            userId: session.user.id,
            roles: resolvedRoles,
            isDemo: false,
            loading: false,
            isAuthenticated: true,
          });
          return;
        }

        // 2. 데모 로그인 체크
        const demo = getDemoAuth();
        if (demo) {
          const { data: profile } = await supabase
            .from('profiles')
            .select('roles, role, caregiver_number')
            .eq('id', demo.userId)
            .maybeSingle();
          // roles(배열)가 null이면 role(텍스트)을 배열로 변환, 둘 다 없으면 demo.roles 사용
          const resolvedRoles: ('patient' | 'caregiver')[] = profile?.roles?.length
            ? (profile.roles as ('patient' | 'caregiver')[])
            : (profile?.role ? [profile.role as ('patient' | 'caregiver')] : []);
          setState({
            userId: demo.userId,
            roles: resolvedRoles.length > 0 ? resolvedRoles : (demo.roles || []),
            isDemo: true,
            loading: false,
            isAuthenticated: true,
          });
          return;
        }

        // 3. 로그인 안 됨
        setState({
          userId: null,
          roles: [],
          isDemo: false,
          loading: false,
          isAuthenticated: false,
        });
        if (requireAuth) {
          navigateRef.current('/app/login', { replace: true });
        }
      } catch {
        setState({
          userId: null,
          roles: [],
          isDemo: false,
          loading: false,
          isAuthenticated: false,
        });
        if (requireAuth) {
          navigateRef.current('/app/login', { replace: true });
        }
      }
    }
    checkAuth();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return state;
}

export async function appLogout() {
  clearDemoAuth();
  await supabase.auth.signOut();
}